import { Routes } from '@angular/router';
import { authGuard, adminGuard, companyOwnerGuard, registerGuard } from '../guards/auth.guard';
import { moduleGuard } from '../guards/module.guard';

export const routes: Routes = [
    {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full'
    },
    {
        path: 'auth/login',
        title: 'Login',
        loadComponent: () =>
            import('../layouts/auth/login/login.component').then((c) => c.LoginComponent),
    },
    {
        path: 'auth/register',
        title: 'Register',
        canActivate: [registerGuard],
        loadComponent: () =>
            import('../layouts/auth/register/register.component').then((c) => c.RegisterComponent),
    },
    {
        path: 'auth/accept-invite',
        title: 'Accept invitation',
        loadComponent: () =>
            import('../layouts/auth/accept-invite/accept-invite.component').then(
                (c) => c.AcceptInviteComponent
            ),
    },
    {
        path: 'reset-password',
        title: 'Reset Password',
        loadComponent: () =>
            import('../layouts/auth/reset-password/reset-password.component').then(
                (c) => c.ResetPasswordComponent
            ),
    },
    {
        path: 'terms',
        title: 'Terms',
        loadComponent: () =>
            import('../layouts/legal/terms/terms.component').then((c) => c.TermsComponent),
    },
    {
        path: 'privacy',
        title: 'Privacy Policy',
        loadComponent: () =>
            import('../layouts/legal/privacy/privacy.component').then((c) => c.PrivacyComponent),
    },
    {
        path: '',
        loadComponent: () =>
            import('../layouts/main-layout/main-layout').then((c) => c.MainLayout),
        canActivate: [authGuard],
        children: [
            {
                path: 'home',
                title: 'Dashboard',
                loadComponent: () =>
                    import('../layouts/home/home').then((c) => c.Home),
            },
            {
                path: 'invoice',
                title: 'Invoice',
                canActivate: [moduleGuard('invoices')],
                loadComponent: () =>
                    import('../layouts/invoice/invoice').then((c) => c.Invoice),
            },
            {
                path: 'invoice-history',
                title: 'Invoice History',
                canActivate: [moduleGuard('invoices')],
                loadComponent: () =>
                    import('../layouts/invoice-history/invoice-history').then((c) => c.InvoiceHistory),
            },
            {
                path: 'invoice-history/view/:id',
                title: 'Invoice Details',
                canActivate: [moduleGuard('invoices')],
                loadComponent: () =>
                    import('../layouts/invoice-history/invoice-details').then((c) => c.InvoiceDetails),
            },
            {
                path: 'companies',
                title: 'Companies',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/companies/companies-list').then((c) => c.CompaniesList),
            },
            {
                path: 'companies/new',
                title: 'Add Company',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/companies/company-entry').then((c) => c.CompanyEntry),
            },
            {
                path: 'companies/view/:id',
                title: 'Company Details',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/companies/company-details').then((c) => c.CompanyDetails),
            },
            {
                path: 'companies/edit/:id',
                title: 'Edit Company',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/companies/company-entry').then((c) => c.CompanyEntry),
            },
            {
                path: 'companies/:id/members',
                title: 'Company Members',
                canActivate: [moduleGuard('members')],
                loadComponent: () =>
                    import('../layouts/companies/company-members/company-members').then((c) => c.CompanyMembers),
            },
            {
                path: 'branches',
                title: 'Branches',
                canActivate: [moduleGuard('branches')],
                loadComponent: () =>
                    import('../layouts/branches/branches-list').then((c) => c.BranchesList),
            },
            {
                path: 'branches/new',
                title: 'Add Branch',
                canActivate: [moduleGuard('branches')],
                loadComponent: () =>
                    import('../layouts/branches/branch-entry').then((c) => c.BranchEntry),
            },
            {
                path: 'companies/:companyId/branches/:branchId/view',
                title: 'Branch Details',
                canActivate: [moduleGuard('branches')],
                loadComponent: () =>
                    import('../layouts/branches/branch-details').then((c) => c.BranchDetails),
            },
            {
                path: 'branches/edit/:id',
                title: 'Edit Branch',
                canActivate: [moduleGuard('branches')],
                loadComponent: () =>
                    import('../layouts/branches/branch-entry').then((c) => c.BranchEntry),
            },
            {
                path: 'companies/:companyId/branches/:branchId/members',
                title: 'Branch Members',
                canActivate: [moduleGuard('branches', 'members')],
                loadComponent: () =>
                    import('../layouts/branches/branch-members/branch-members').then((c) => c.BranchMembers),
            },
            {
                path: 'products',
                title: 'Products',
                canActivate: [moduleGuard('products')],
                loadComponent: () =>
                    import('../layouts/products/products-list').then((c) => c.ProductsList),
            },
            {
                path: 'products/new',
                title: 'Add Product',
                canActivate: [moduleGuard('products')],
                loadComponent: () =>
                    import('../layouts/products/products-entry').then((c) => c.ProductsEntry),
            },
            {
                path: 'products/view/:id',
                title: 'Product Details',
                canActivate: [moduleGuard('products')],
                loadComponent: () =>
                    import('../layouts/products/product-details').then((c) => c.ProductDetails),
            },
            {
                path: 'products/edit/:id',
                title: 'Edit Product',
                canActivate: [moduleGuard('products')],
                loadComponent: () =>
                    import('../layouts/products/products-entry').then((c) => c.ProductsEntry),
            },
            {
                path: 'inventory',
                title: 'Inventory',
                canActivate: [moduleGuard('inventory')],
                loadComponent: () =>
                    import('../layouts/inventory/inventory-list').then((c) => c.InventoryList),
            },
            {
                path: 'inventory/new',
                title: 'Add Inventory Item',
                canActivate: [moduleGuard('inventory')],
                loadComponent: () =>
                    import('../layouts/inventory/inventory-entry').then((c) => c.InventoryEntry),
            },
            {
                path: 'inventory/edit/:id',
                title: 'Edit Inventory Item',
                canActivate: [moduleGuard('inventory')],
                loadComponent: () =>
                    import('../layouts/inventory/inventory-entry').then((c) => c.InventoryEntry),
            },
            {
                path: 'inventory/alerts',
                title: 'Low Stock Alerts',
                canActivate: [moduleGuard('inventory')],
                loadComponent: () =>
                    import('../layouts/inventory/inventory-alerts/inventory-alerts').then((c) => c.InventoryAlerts),
            },
            {
                path: 'inventory/stock-transfer',
                title: 'Stock Transfer',
                canActivate: [moduleGuard('inventory')],
                loadComponent: () =>
                    import('../layouts/inventory/stock-transfer').then((c) => c.StockTransfer),
            },
            {
                path: 'inventory/adjust',
                title: 'Stock Adjustment',
                canActivate: [moduleGuard('inventory')],
                loadComponent: () =>
                    import('../layouts/inventory/inventory-adjust').then((c) => c.InventoryAdjust),
            },
            {
                path: 'inventory/import',
                title: 'Import Inventory',
                canActivate: [moduleGuard('inventory')],
                loadComponent: () =>
                    import('../layouts/inventory/inventory-import').then((c) => c.InventoryImport),
            },
            {
                path: 'inventory/integrity',
                title: 'Inventory Integrity Console',
                canActivate: [moduleGuard('inventory')],
                loadComponent: () =>
                    import('../layouts/inventory/inventory-integrity-console').then((c) => c.InventoryIntegrityConsole),
            },
            {
                path: 'inventory/product-on-hand',
                title: 'Product on Hand',
                loadComponent: () =>
                    import('../layouts/inventory/product-on-hand').then((c) => c.ProductOnHand),
            },
            {
                path: 'warehouses',
                title: 'Warehouse List',
                canActivate: [moduleGuard('warehouses')],
                loadComponent: () =>
                    import('../layouts/warehouses/warehouses-list').then((c) => c.WarehousesList),
            },
            {
                path: 'warehouses/new',
                title: 'Add Warehouse',
                canActivate: [moduleGuard('warehouses')],
                loadComponent: () =>
                    import('../layouts/warehouses/warehouse-entry').then((c) => c.WarehouseEntry),
            },
            {
                path: 'warehouses/edit/:id',
                title: 'Edit Warehouse',
                canActivate: [moduleGuard('warehouses')],
                loadComponent: () =>
                    import('../layouts/warehouses/warehouse-entry').then((c) => c.WarehouseEntry),
            },
            {
                path: 'stock-ledger',
                title: 'Stock Ledger',
                canActivate: [moduleGuard('inventory')],
                loadComponent: () =>
                    import('../app/modules/inventory/stock-transactions/stock-transactions.component').then((c) => c.StockTransactionsComponent),
            },
            {
                path: 'users',
                title: 'Users',
                canActivate: [companyOwnerGuard],
                loadComponent: () =>
                    import('../layouts/users/users-list').then((c) => c.UsersList),
            },
            {
                path: 'users/new',
                title: 'Create User',
                canActivate: [companyOwnerGuard],
                loadComponent: () =>
                    import('../layouts/users/user-entry').then((c) => c.UserEntry),
            },
            {
                path: 'users/view/:id',
                title: 'User Details',
                canActivate: [companyOwnerGuard],
                loadComponent: () =>
                    import('../layouts/users/user-details').then((c) => c.UserDetails),
            },
            {
                path: 'users/edit/:id',
                title: 'Edit User',
                canActivate: [companyOwnerGuard],
                loadComponent: () =>
                    import('../layouts/users/user-entry').then((c) => c.UserEntry),
            },
            {
                path: 'customers',
                title: 'Customers',
                canActivate: [moduleGuard('customers')],
                loadComponent: () =>
                    import('../layouts/customers/customers-list').then((c) => c.CustomersList),
            },
            {
                path: 'customers/new',
                title: 'Add Customer',
                canActivate: [moduleGuard('customers')],
                loadComponent: () =>
                    import('../layouts/customers/customer-entry').then((c) => c.CustomerEntry),
            },
            {
                path: 'customers/view/:id',
                title: 'Customer Details',
                canActivate: [moduleGuard('customers')],
                loadComponent: () =>
                    import('../layouts/customers/customer-details').then((c) => c.CustomerDetails),
            },
            {
                path: 'customers/edit/:id',
                title: 'Edit Customer',
                canActivate: [moduleGuard('customers')],
                loadComponent: () =>
                    import('../layouts/customers/customer-entry').then((c) => c.CustomerEntry),
            },
            {
                path: 'reports',
                title: 'Reports & Analytics',
                canActivate: [moduleGuard('reports')],
                loadComponent: () =>
                    import('../layouts/reports/reports').then((c) => c.Reports),
            },
            {
                path: 'operations/async-jobs',
                title: 'Async Jobs',
                canActivate: [authGuard],
                loadComponent: () =>
                    import('../layouts/operations/async-jobs/async-jobs-page').then((c) => c.AsyncJobsPage),
            },
            {
                path: 'profile',
                title: 'My Profile',
                loadComponent: () =>
                    import('../layouts/profile/profile').then((c) => c.Profile),
            },
            {
                path: 'settings',
                title: 'Settings',
                loadComponent: () =>
                    import('../layouts/settings/settings-page').then((c) => c.SettingsPage),
            },
            {
                path: 'settings/system',
                title: 'System Settings',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/settings/system-settings').then((c) => c.SystemSettings),
            },
            {
                path: 'settings/notifications',
                title: 'Notification Settings',
                canActivate: [moduleGuard('notifications')],
                loadComponent: () =>
                    import('../layouts/settings/notification-settings').then((c) => c.NotificationSettings),
            },
            {
                path: 'audit-log',
                title: 'Audit Log',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/audit-log/audit-log').then((c) => c.AuditLog),
            },
            {
                path: 'roles',
                title: 'Role Management',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/roles/roles-list').then((c) => c.RolesList),
            },
            {
                path: 'roles/new',
                title: 'New Role',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/roles/role-entry').then((c) => c.RoleEntry),
            },
            {
                path: 'roles/edit/:id',
                title: 'Edit Role',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/roles/role-entry').then((c) => c.RoleEntry),
            },
            {
                path: 'user-permissions',
                title: 'User Permissions',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/users/user-permissions').then((c) => c.UserPermissions),
            },
            {
                path: 'email-templates',
                title: 'Email Templates',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/email-templates/email-templates-list').then((c) => c.EmailTemplatesList),
            },
            {
                path: 'email-templates/new',
                title: 'New Email Template',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/email-templates/email-template-entry').then((c) => c.EmailTemplateEntry),
            },
            {
                path: 'email-templates/edit/:id',
                title: 'Edit Email Template',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/email-templates/email-template-entry').then((c) => c.EmailTemplateEntry),
            },
            {
                path: 'payment-reminders',
                title: 'Payment Reminders',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/payment-reminders/payment-reminders-list').then((c) => c.PaymentRemindersList),
            },
            {
                path: 'payment-reminders/new',
                title: 'New Payment Reminder',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/payment-reminders/payment-reminder-entry').then((c) => c.PaymentReminderEntry),
            },
            {
                path: 'payment-reminders/edit/:id',
                title: 'Edit Payment Reminder',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/payment-reminders/payment-reminder-entry').then((c) => c.PaymentReminderEntry),
            },
            {
                path: 'report-templates',
                title: 'Report Templates',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/report-templates/report-templates-list').then((c) => c.ReportTemplatesList),
            },
            {
                path: 'report-templates/new',
                title: 'New Report Template',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/report-templates/report-template-entry').then((c) => c.ReportTemplateEntry),
            },
            {
                path: 'report-templates/edit/:id',
                title: 'Edit Report Template',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/report-templates/report-template-entry').then((c) => c.ReportTemplateEntry),
            },
            {
                path: 'scheduled-reports',
                title: 'Scheduled Reports',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/scheduled-reports/scheduled-reports-list').then((c) => c.ScheduledReportsList),
            },
            {
                path: 'scheduled-reports/new',
                title: 'New Scheduled Report',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/scheduled-reports/scheduled-report-entry').then((c) => c.ScheduledReportEntry),
            },
            {
                path: 'scheduled-reports/edit/:id',
                title: 'Edit Scheduled Report',
                canActivate: [adminGuard],
                loadComponent: () =>
                    import('../layouts/scheduled-reports/scheduled-report-entry').then((c) => c.ScheduledReportEntry),
            },
            {
                path: 'purchase-orders',
                title: 'Purchase Orders',
                canActivate: [moduleGuard('purchaseOrders')],
                loadComponent: () =>
                    import('../layouts/purchase-orders/purchase-orders-list').then((c) => c.PurchaseOrdersList),
            },
            {
                path: 'purchase-orders/new',
                title: 'New Purchase Order',
                canActivate: [moduleGuard('purchaseOrders')],
                loadComponent: () =>
                    import('../layouts/purchase-orders/purchase-order-entry').then((c) => c.PurchaseOrderEntry),
            },
            {
                path: 'purchase-orders/view/:id',
                title: 'Purchase Order Details',
                canActivate: [moduleGuard('purchaseOrders')],
                loadComponent: () =>
                    import('../layouts/purchase-orders/purchase-order-details').then((c) => c.PurchaseOrderDetails),
            },
            {
                path: 'purchase-orders/edit/:id',
                title: 'Edit Purchase Order',
                canActivate: [moduleGuard('purchaseOrders')],
                loadComponent: () =>
                    import('../layouts/purchase-orders/purchase-order-entry').then((c) => c.PurchaseOrderEntry),
            },
            {
                path: 'delivery-notes',
                title: 'Delivery Notes',
                canActivate: [moduleGuard('deliveryNotes')],
                loadComponent: () =>
                    import('../layouts/delivery-notes/delivery-notes-list').then((c) => c.DeliveryNotesList),
            },
            {
                path: 'delivery-notes/new',
                title: 'New Delivery Note',
                canActivate: [moduleGuard('deliveryNotes')],
                loadComponent: () =>
                    import('../layouts/delivery-notes/delivery-note-entry').then((c) => c.DeliveryNoteEntry),
            },
            {
                path: 'delivery-notes/view/:id',
                title: 'Delivery Note Details',
                canActivate: [moduleGuard('deliveryNotes')],
                loadComponent: () =>
                    import('../layouts/delivery-notes/delivery-note-details').then((c) => c.DeliveryNoteDetails),
            },
            {
                path: 'delivery-notes/edit/:id',
                title: 'Edit Delivery Note',
                canActivate: [moduleGuard('deliveryNotes')],
                loadComponent: () =>
                    import('../layouts/delivery-notes/delivery-note-entry').then((c) => c.DeliveryNoteEntry),
            }
        ]
    },
    {
        path: 'auth',
        redirectTo: 'auth/login',
        pathMatch: 'full'
    },
];
